import { FormEvent, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { SHARE_LOCKED_EVENT } from '../../graphql/client';
import BrandMark from '../../components/common/BrandMark';
import styles from './ShareGate.module.css';

type GateState = 'checking' | 'locked' | 'open';

// Wraps every /share/:token route. This is only the prompt — the backend
// refuses each request of a locked link on its own, so a failed check simply
// falls through to the page, which then shows its usual error state.
const ShareGate = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<GateState>('checking');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const base = `/api/share/${encodeURIComponent(token ?? '')}`;

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${base}/access`);
        const body = res.ok ? await res.json() : null;
        if (!cancelled) setState(body?.passwordRequired ? 'locked' : 'open');
      } catch {
        if (!cancelled) setState('open');
      }
    };
    setState('checking');
    check();
    return () => { cancelled = true; };
  }, [base]);

  useEffect(() => {
    const relock = () => setState('locked');
    window.addEventListener(SHARE_LOCKED_EVENT, relock);
    return () => window.removeEventListener(SHARE_LOCKED_EVENT, relock);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${base}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setPassword('');
        setState('open');
        return;
      }
      const body = await res.json().catch(() => ({}));
      setError(body.error || 'Could not unlock this link');
    } catch {
      setError('Could not reach the server — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'open') return <Outlet />;

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.logoIcon}>
          <BrandMark size={34} decorative />
        </div>
        <span className={styles.logoText}>Infrio</span>
      </header>

      <main className={styles.main}>
        {state === 'checking' ? (
          <span className="material-symbols-outlined animate-spin text-4xl text-primary">refresh</span>
        ) : (
          <form className={styles.card} onSubmit={submit}>
            <div className={styles.lockIcon}>
              <span className="material-symbols-outlined text-3xl">lock</span>
            </div>
            <h1 className={styles.title}>Password required</h1>
            <p className={styles.desc}>
              This shared link is protected. Enter the password you received to continue.
            </p>
            <input
              className="input-field"
              type="password"
              placeholder="Password"
              autoFocus
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className={styles.error}>{error}</p>}
            <button
              type="submit"
              disabled={!password || submitting}
              className={`btn-primary px-5 py-2.5 rounded-lg text-sm disabled:opacity-50 ${styles.submit}`}
            >
              {submitting ? 'Unlocking…' : 'Unlock'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default ShareGate;
